// src/views/QuestionsList.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { questionsAPI, plateformesAPI, formationsAPI, domainesExpertiseAPI, learndashSyncAPI } from '../api/api';

function QuestionsList({ formations }) {
  const [questions, setQuestions] = useState([]);
  const [plateformes, setPlateformes] = useState([]);
  const [domainesExpertise, setDomainesExpertise] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingQuestions, setDeletingQuestions] = useState(new Set());
  const [syncingQuestions, setSyncingQuestions] = useState(new Set());
  const [syncStatuses, setSyncStatuses] = useState({});
  const [selectedQuestions, setSelectedQuestions] = useState(new Set());
  const [showBulkSyncModal, setShowBulkSyncModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    plateforme_id: '',
    formation_id: '',
    domaine_expertise_id: '',
    search: '',
    donnees_dynamiques: false
  });

  // Use useMemo to automatically filter questions when dependencies change
  const filteredQuestions = useMemo(() => {
    let filtered = questions;

    // Filtrer par plateforme
    if (filters.plateforme_id) {
      filtered = filtered.filter(question =>
        question.plateformes_noms && question.plateformes_noms.includes(
          plateformes.find(p => p.id == filters.plateforme_id)?.nom
        )
      );
    }

    // Filtrer par formation
    if (filters.formation_id) {
      filtered = filtered.filter(question =>
        question.formations_noms && question.formations_noms.includes(
          formations.find(f => f.id == filters.formation_id)?.nom
        )
      );
    }

    // Filtrer par module
    if (filters.domaine_expertise_id) {
      filtered = filtered.filter(question =>
        question.domaines_expertise_noms && question.domaines_expertise_noms.includes(
          domainesExpertise.find(de => de.id == filters.domaine_expertise_id)?.titre
        )
      );
    }

    // Filtrer par recherche textuelle
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(question =>
        question.intitule.toLowerCase().includes(searchTerm) ||
        (question.feedback && question.feedback.toLowerCase().includes(searchTerm))
      );
    }

    // Filtrer par données dynamiques
    if (filters.donnees_dynamiques) {
      filtered = filtered.filter(question => question.donnees_dynamiques === true);
    }

    return filtered;
  }, [questions, plateformes, formations, domainesExpertise, filters]);

  // Clear selected questions that are no longer visible when filtered questions change
  useEffect(() => {
    const newSelectedSet = new Set();
    selectedQuestions.forEach(id => {
      if (filteredQuestions.find(q => q.id === id)) {
        newSelectedSet.add(id);
      }
    });
    if (newSelectedSet.size !== selectedQuestions.size) {
      setSelectedQuestions(newSelectedSet);
    }
  }, [filteredQuestions]);

  // Toggle selection of a question for bulk sync
  const toggleQuestionSelection = (questionId, e) => {
    e.stopPropagation(); // Empêcher le clic de naviguer vers la question
    setSelectedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  // Select all visible questions
  const selectAllQuestions = (e) => {
    e.preventDefault();
    if (selectedQuestions.size === filteredQuestions.length) {
      setSelectedQuestions(new Set());
    } else {
      setSelectedQuestions(new Set(filteredQuestions.map(q => q.id)));
    }
  };

  // Gérer le changement des filtres
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setFilters({
      plateforme_id: '',
      formation_id: '',
      domaine_expertise_id: '',
      search: '',
      donnees_dynamiques: false
    });
  };

  const handleEditQuestion = (question) => {
    // Open in new tab if middle click or ctrl/cmd click
    navigate(`/questions/edit/${question.id}`, { state: { question } });
  };

  // Load all data on mount
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [questionsData, plateformesData, domainesData] = await Promise.all([
        questionsAPI.getAll(),
        plateformesAPI.getAll(),
        domainesExpertiseAPI.getAll()
      ]);

      // Trier les questions par date de dernière modification (plus récente en premier)
      const sortedQuestions = questionsData.sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at);
        const dateB = new Date(b.updated_at || b.created_at);
        return dateB - dateA;
      });

      setQuestions(sortedQuestions);
      setPlateformes(plateformesData);
      setDomainesExpertise(domainesData);

      // Load sync statuses for all questions (in background)
      sortedQuestions.forEach(q => loadSyncStatus(q.id));
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  // Bulk sync selected questions
  const handleBulkSync = async () => {
    if (!selectedPlatform) {
      alert('Veuillez sélectionner une plateforme');
      return;
    }
    if (selectedQuestions.size === 0) {
      alert('Veuillez sélectionner au moins une question');
      return;
    }

    const platformConfig = JSON.parse(selectedPlatform);

    setBulkSyncing(true);
    try {
      const results = [];
      for (const questionId of selectedQuestions) {
        try {
          const result = await learndashSyncAPI.syncQuestionToPlatform(questionId, platformConfig);
          results.push({ questionId, ...result });
        } catch (error) {
          results.push({ questionId, success: false, error: error.message });
        }
      }

      const successCount = results.filter(r => r.success).length;
      const failCount = results.length - successCount;
      
      let message = `Synchronisation terminée !\n\n✅ ${successCount}/${results.length} questions synchronisées`;
      if (failCount > 0) {
        message += `\n❌ ${failCount} échec(s)`;
      }
      
      alert(message);
      setShowBulkSyncModal(false);
      setSelectedQuestions(new Set());
      setSelectedPlatform('');
      
      // Refresh sync statuses
      selectedQuestions.forEach(qid => loadSyncStatus(qid));
    } catch (error) {
      console.error('Erreur lors de la synchro en masse:', error);
      alert(`Erreur: ${error.message}`);
    } finally {
      setBulkSyncing(false);
    }
  };

  // Handle right-click context menu for opening in new tab
  const handleQuestionContextMenu = (e, questionId) => {
    e.preventDefault();
    // Let the default browser context menu handle it
    return true;
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [copiedQuestionId, setCopiedQuestionId] = useState(null);

  const handleCopyTitle = (e, question) => {
    e.stopPropagation();

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(question.intitule);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = question.intitule;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }

    setCopiedQuestionId(question.id);
    setTimeout(() => {
      setCopiedQuestionId(prev => (prev === question.id ? null : prev));
    }, 1500);
  };

  // Load sync status for a question
  const loadSyncStatus = async (questionId) => {
    try {
      const status = await learndashSyncAPI.getSyncStatus(questionId);
      setSyncStatuses(prev => ({ ...prev, [questionId]: status }));
      return status;
    } catch (error) {
      console.error(`Error loading sync status for question ${questionId}:`, error);
      return null;
    }
  };

  const handleDeleteQuestion = async (questionId, e) => {
    if (e) {
      e.stopPropagation();
    }
    const question = questions.find(q => q.id === questionId);
    
    // Vérifier d'abord si la question est synchronisée
    try {
      const syncStatus = await learndashSyncAPI.getSyncStatus(questionId);
      if (syncStatus.isSynced && syncStatus.syncCount > 0) {
        alert(`⚠️ Impossible de supprimer cette question car elle est synchronisée avec ${syncStatus.syncCount} plateforme(s).\n\nVeuillez d'abord la désynchroniser avant de la supprimer.`);
        return;
      }
    } catch (syncError) {
      console.error('Erreur lors de la vérification de la synchronisation:', syncError);
      // On continue quand même, au cas où l'endpoint ne serait pas disponible
    }
    
    setQuestionToDelete({id: questionId, intitule: question?.intitule || ''});
    setShowDeleteModal(true);
  };

  const handleSyncQuestion = async (questionId, e) => {
    e.stopPropagation(); // Empêcher le clic de naviguer vers la question

    // Add the question to the syncing set to show loading state
    setSyncingQuestions(prev => new Set(prev).add(questionId));

    try {
      const result = await learndashSyncAPI.syncQuestion(questionId);

      if (result.success) {
        alert(`Question synchronisée avec succès : ${result.action === 'created' ? 'créée' : 'mise à jour'} dans LearnDash`);
        // Refresh sync status after successful sync
        await loadSyncStatus(questionId);
      } else {
        alert(`Erreur lors de la synchronisation : ${result.message}`);
      }
    } catch (error) {
      console.error('Erreur lors de la synchronisation:', error);
      alert(`Erreur lors de la synchronisation : ${error.message || 'Une erreur inconnue est survenue'}`);
    } finally {
      // Remove from syncing set after completion
      setSyncingQuestions(prev => {
        const newSet = new Set(prev);
        newSet.delete(questionId);
        return newSet;
      });
    }
  };

  const handleViewSyncDetails = async (questionId, e) => {
    e.stopPropagation();
    const status = await loadSyncStatus(questionId);
    if (status) {
      const platforms = status.platforms || [];
      if (platforms.length === 0) {
        alert('Cette question n\'est synchronisée avec aucune plateforme.');
      } else {
        const platformList = platforms.map(p => {
          const link = p.platform_question_id ? `${p.platform_instance}/wp-admin/post.php?post=${p.platform_question_id}&action=edit` : p.platform_instance;
          return `- ${p.platform_instance} (${p.sync_status})\n  Lien: ${link}`;
        }).join('\n');
        alert(`Question synchronisée avec ${platforms.length} plateforme(s):\n\n${platformList}`);
      }
    }
  };

  const confirmDelete = async () => {
    if (questionToDelete) {
      try {
        // Add the question to the deleting set to trigger animation
        setDeletingQuestions(prev => new Set(prev).add(questionToDelete.id));

        await questionsAPI.remove(questionToDelete.id);

        // Remove the question from the state - filteredQuestions will auto-update via useMemo
        setQuestions(prev => prev.filter(q => q.id !== questionToDelete.id));

        setShowDeleteModal(false);
        setQuestionToDelete(null);
      } catch (error) {
        console.error('Erreur lors de la suppression de la question:', error);
        alert(`❌ ${error.message || 'Erreur lors de la suppression'}`);
      } finally {
        // Remove from deleting set after a delay to allow animation to complete
        setTimeout(() => {
          setDeletingQuestions(prev => {
            const newSet = new Set(prev);
            newSet.delete(questionToDelete?.id);
            return newSet;
          });
        }, 300); // Match the animation duration
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setQuestionToDelete(null);
  };

  if (loading) {
    return (
      <div className="px-4 py-6 sm:px-0 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <h2 className="text-2xl font-bold text-gray-900">Liste des questions</h2>
          <span className="text-sm text-gray-500">{filteredQuestions.length} question{filteredQuestions.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center space-x-3">
          {selectedQuestions.size > 0 && (
            <button
              onClick={() => setShowBulkSyncModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
              Synchro ({selectedQuestions.size})
            </button>
          )}
          <button
            onClick={() => navigate('/questions/create')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition"
          >
            Créer une question
          </button>
        </div>
      </div>

      {/* Section des filtres */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Recherche</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Rechercher dans les questions..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plateforme</label>
            <select
              value={filters.plateforme_id}
              onChange={(e) => handleFilterChange('plateforme_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Toutes les plateformes</option>
              {plateformes.map(plateforme => (
                <option key={plateforme.id} value={plateforme.id}>
                  {plateforme.nom}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Formation</label>
            <select
              value={filters.formation_id}
              onChange={(e) => handleFilterChange('formation_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Toutes les formations</option>
              {formations.map(formation => (
                <option key={formation.id} value={formation.id}>
                  {formation.nom}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Module</label>
            <select
              value={filters.domaine_expertise_id}
              onChange={(e) => handleFilterChange('domaine_expertise_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Tous les modules</option>
              {domainesExpertise.map(domaine => (
                <option key={domaine.id} value={domaine.id}>
                  {domaine.titre} {domaine.parent_formation_nom ? `(${domaine.parent_formation_nom})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={filters.donnees_dynamiques}
                onChange={(e) => handleFilterChange('donnees_dynamiques', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500 relative"></div>
              <span className="ml-3 text-sm font-medium text-gray-700">
                Données dynamiques uniquement
              </span>
            </label>
          </div>
          
          <button
            onClick={resetFilters}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
          >
            Réinitialiser les filtres
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-200">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={selectedQuestions.size === filteredQuestions.length && filteredQuestions.length > 0}
              onChange={selectAllQuestions}
              className="sr-only peer"
            />
            <div className="w-4 h-4 bg-gray-200 peer-focus:outline-none rounded peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-blue-500 relative"></div>
            <span className="ml-2 text-sm font-medium text-gray-700">
              Tout sélectionner ({selectedQuestions.size}/{filteredQuestions.length})
            </span>
          </label>
          <span className="text-sm text-gray-500 italic">
            {filteredQuestions.length} question{filteredQuestions.length !== 1 ? 's' : ''} {questions.length !== filteredQuestions.length ? `(sur ${questions.length})` : ''}
          </span>
        </div>

        <div className="space-y-3" key={`questions-list-${filteredQuestions.length}`}>
          {filteredQuestions.length > 0 ? (
            filteredQuestions.map((question) => (
              <div
                key={question.id}
                onClick={() => navigate(`/questions/edit/${question.id}`, { state: { question } })}
                className={`block border border-gray-200 rounded-lg p-3 hover:shadow-md transition cursor-pointer flex items-start ${
                  deletingQuestions.has(question.id)
                    ? 'opacity-0 scale-95 transition-all duration-300 ease-in-out'
                    : 'opacity-100 scale-100'
                } ${
                  selectedQuestions.has(question.id)
                    ? 'bg-blue-50 border-blue-300'
                    : 'bg-white'
                }`}
              >
                {/* Checkbox de sélection */}
                <div className="flex-shrink-0 mr-3 mt-1">
                  <input
                    type="checkbox"
                    checked={selectedQuestions.has(question.id)}
                    onChange={(e) => toggleQuestionSelection(question.id, e)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>
                {/* Colonne de gauche - Date de modification et utilisateur */}
                <div className="flex flex-col items-center mr-3">
                  {question.updated_at && (
                    <span className="text-xs text-gray-500 text-center" title={new Date(question.updated_at).toLocaleString('fr-FR')}>
                      {new Date(question.updated_at).toLocaleDateString('fr-FR')}<br/>
                      {new Date(question.updated_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                  {question.updated_by_email && (
                    <span className="text-xs text-gray-600 text-center mt-1" title={question.updated_by_email}>
                      {question.updated_by_email.split('@')[0]}
                    </span>
                  )}
                </div>

                {/* Colonne centrale - Contenu principal */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <h3 className="font-medium text-gray-900 truncate flex-1">{question.intitule}</h3>
                    <button
                      onClick={(e) => handleCopyTitle(e, question)}
                      className={copiedQuestionId === question.id ? 'text-green-600' : 'text-gray-400 hover:text-gray-600'}
                      title="Copier l'intitulé de la question"
                    >
                      {copiedQuestionId === question.id ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M8 3a1 1 0 00-1 1v1H5a2 2 0 00-2 2v9a2 2 0 002 2h8a2 2 0 002-2v-1h1a2 2 0 002-2V7.414a2 2 0 00-.586-1.414l-3.414-3.414A2 2 0 0012.586 2H9a1 1 0 00-1 1zm5.586 1L16 6.414V15a1 1 0 01-1 1h-1V7a2 2 0 00-2-2H9V4h4.586zM5 6h6a1 1 0 011 1v9a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1z" />
                        </svg>
                      )}
                    </button>
                    {question.donnees_dynamiques && (
                      <span className="bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded text-xs font-medium" title="Cette question contient des données dynamiques qui peuvent changer fréquemment">
                        ⚠️ Données dynamiques
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center mt-1 space-x-1 text-xs text-gray-500">
                    <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">{question.type_reponse}</span>
                  </div>
                </div>

                {/* Colonne de droite - Plateforme et actions */}
                <div className="flex flex-col items-end ml-2">
                  {question.plateformes_noms && (
                    <span className="text-xs bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded mb-1" title={question.plateformes_noms}>
                      {question.plateformes_noms}
                    </span>
                  )}
                  <div className="flex items-center space-x-1">
                    {/* Sync status indicator */}
                    <button
                      onClick={(e) => handleViewSyncDetails(question.id, e)}
                      className={`${
                        syncingQuestions.has(question.id)
                          ? 'text-gray-400'
                          : syncStatuses[question.id]?.isSynced
                            ? 'text-green-600 hover:text-green-800'
                            : 'text-gray-400 hover:text-gray-600'
                      }`}
                      title={
                        syncStatuses[question.id]?.isSynced
                          ? `Synchronisé avec ${syncStatuses[question.id].syncCount} plateforme(s) - Cliquer pour voir les détails`
                          : 'Non synchronisé'
                      }
                    >
                      {syncingQuestions.has(question.id) ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <circle cx="12" cy="12" r="10" strokeWidth="2" strokeOpacity="0.25"/>
                          <path d="M12 2a10 10 0 0 1 10 10" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      ) : syncStatuses[question.id]?.isSynced ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    {/* Sync button */}
                    <button
                      onClick={(e) => handleSyncQuestion(question.id, e)}
                      disabled={syncingQuestions.has(question.id)}
                      className={`${
                        syncingQuestions.has(question.id)
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-green-500 hover:text-green-700'
                      }`}
                      title="Synchroniser avec LearnDash"
                    >
                      {syncingQuestions.has(question.id) ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 animate-spin" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    {/* Delete button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Empêcher le clic de se propager au parent
                        handleDeleteQuestion(question.id, e);
                      }}
                      className="text-red-500 hover:text-red-700"
                      title="Supprimer la question"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">Aucune question trouvée avec les filtres appliqués</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative top-0 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Confirmation de suppression</h3>
              <div className="mt-2">
                <p className="text-sm text-gray-600">
                  Êtes-vous sûr de vouloir supprimer la question ?
                </p>
              </div>
              <div className="items-center gap-2 mt-4 flex justify-end">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 focus:outline-none"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de synchronisation en masse */}
      {showBulkSyncModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative top-0 mx-auto p-6 border w-[500px] shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                🔄 Synchroniser {selectedQuestions.size} question{selectedQuestions.size !== 1 ? 's' : ''}
              </h3>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3">
                  Sélectionnez la plateforme avec laquelle synchroniser les questions :
                </p>
                
                <select
                  value={selectedPlatform}
                  onChange={(e) => setSelectedPlatform(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
                  autoFocus
                >
                  <option value="">-- Choisir une plateforme --</option>
                  {plateformes.map(plateforme => (
                    <option key={plateforme.id} value={JSON.stringify({
                      id: plateforme.id,
                      nom: plateforme.nom,
                      wordpress_url: plateforme.wordpress_url,
                      wordpress_username: plateforme.wordpress_username,
                      wordpress_password: plateforme.wordpress_password
                    })}>
                      {plateforme.nom}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                <p className="text-sm text-blue-800">
                  ℹ️ Seules les questions non synchronisées avec cette plateforme seront mises à jour.
                </p>
              </div>

              <div className="items-center gap-2 mt-6 flex justify-end">
                <button
                  onClick={() => {
                    setShowBulkSyncModal(false);
                    setSelectedPlatform('');
                  }}
                  disabled={bulkSyncing}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 focus:outline-none disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleBulkSync}
                  disabled={bulkSyncing || !selectedPlatform}
                  className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none disabled:opacity-50 flex items-center"
                >
                  {bulkSyncing ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Synchronisation...
                    </>
                  ) : (
                    'Lancer la synchronisation'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QuestionsList;